/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useIVRList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Custom hook for managing IVRs list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchIVRs } from '../api/ivrApi';
import { IVR } from '../types';

interface UseIVRListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing IVRs list
 * Implements Domain Contextual Filtering pattern
 */
export function useIVRList(options: UseIVRListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchIVRsData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchIVRs(
          {
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            domainId: effectiveDomainId,
          },
          params.search,
          params.sortBy,
        );

        return {
          data: result.data as IVR[],
          pagination: result.pagination,
        };
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('ivrs:messages.loadError');
        showError(message);
        return {
          data: [] as IVR[],
          pagination: {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0,
          },
        };
      }
    },
    [showError, t, effectiveDomainId],
  );

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('ivrs:columns.name'),
        visible: true,
        sortable: true,
      },
      {
        key: 'context',
        label: t('ivrs:columns.context'),
        visible: true,
        sortable: true,
      },
      {
        key: 'timeout',
        label: t('ivrs:columns.timeout'),
        visible: true,
        sortable: true,
      },
      {
        key: 'enabled',
        label: t('ivrs:columns.enabled'),
        visible: true,
        sortable: true,
      },
      {
        key: 'actions',
        label: t('ivrs:columns.actions'),
        visible: true,
        sortable: false,
      },
    ],
    [t],
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData: fetchIVRsData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as IVR[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

